import React, { useEffect, useRef, useState } from "react";

interface InputProps extends React.ComponentPropsWithoutRef<"input"> {
  label?: string;
  className?: string;
}

function formatDateInput(value: string, rawInput: string): string {
  const digits = value.replace(/\D/g, "").slice(0, 8);
  const len = digits.length;

  if (len < 4) return digits;
  if (len === 4 && rawInput.endsWith("/")) return digits;
  if (len === 4) return `${digits}/`;
  if (len < 6) return `${digits.slice(0, 4)}/${digits.slice(4)}`;
  if (len === 6 && rawInput.endsWith("/"))
    return `${digits.slice(0, 4)}/${digits.slice(4, 6)}`;
  if (len === 6) return `${digits.slice(0, 4)}/${digits.slice(4, 6)}/`;
  return `${digits.slice(0, 4)}/${digits.slice(4, 6)}/${digits.slice(6)}`;
}

/** 메인페이지 년월일 입력창 */
export function CustomFormatInput(props: InputProps) {
  const { label, className, value, onChange, ...rest } = props;
  const inputRef = useRef<HTMLInputElement>(null);
  const [rawInput, setRawInput] = useState("");
  const [displayValue, setDisplayValue] = useState(() =>
    formatDateInput(String(value), "")
  );

  // Removed the useEffect that reset displayValue from props to prevent flicker

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    const numbersOnly = raw.replace(/\D/g, "").slice(0, 8);
    const formatted = formatDateInput(numbersOnly, raw);
    setRawInput(raw);
    setDisplayValue(formatted);

    if (onChange) {
      const syntheticEvent = {
        ...e,
        target: {
          ...e.target,
          value: numbersOnly,
        },
      } as React.ChangeEvent<HTMLInputElement>;
      onChange(syntheticEvent);
    }
  };

  useEffect(() => {
    if (inputRef.current) {
      const input = inputRef.current;

      const handleFocus = () => {
        // 입력 값의 길이를 가져와서 커서를 이동
        input.value = "";
        const length = input.value.length;
        input.setSelectionRange(length, length);
      };

      input.addEventListener("focus", handleFocus);

      // 컴포넌트가 언마운트될 때 이벤트 리스너 제거
      return () => {
        input.removeEventListener("focus", handleFocus);
      };
    }
  }, []);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const input = inputRef.current;
    if (!input) return;

    const cursorPos = input.selectionStart ?? 0;
    const currentValue = input.value;

    if (
      e.key === "Backspace" &&
      cursorPos > 0 &&
      currentValue[cursorPos - 1] === "/"
    ) {
      e.preventDefault();

      // Remove slash and previous digit
      const newRawInput =
        currentValue.slice(0, cursorPos - 2) + currentValue.slice(cursorPos);
      const numbersOnly = newRawInput.replace(/\D/g, "").slice(0, 8);
      const formatted = formatDateInput(numbersOnly, newRawInput);

      setRawInput(newRawInput);
      setDisplayValue(formatted);

      if (onChange) {
        const syntheticEvent = {
          ...e,
          target: {
            ...input,
            value: numbersOnly,
          },
        } as React.ChangeEvent<HTMLInputElement>;
        onChange(syntheticEvent);
      }

      requestAnimationFrame(() => {
        if (inputRef.current) {
          inputRef.current.setSelectionRange(cursorPos - 2, cursorPos - 2);
        }
      });
    }

    // 부모에서 전달된 onKeyDown도 호출
    if (props.onKeyDown) {
      props.onKeyDown(e);
    }
  };

  return (
    <div
      className={
        "relative border-2 border-slate-400 flex items-center " + className
      }
      style={{ borderRadius: 6 }}
    >
      <span className="absolute bg-white leading-none -top-2 sm:-top-[0.5vw] left-1 text-slate-400 text-xs">
        {label}
      </span>
      <input
        ref={inputRef}
        {...rest}
        value={displayValue}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        className="w-full h-full text-left outline-none px-1 py-2"
        style={{ borderRadius: 6 }}
      />
    </div>
  );
}

/** 메인페이지 입력창 */
// export default function CustomInput(props: InputProps) {
//   const { label, className } = props;
//   const inputRef = useRef<HTMLInputElement>(null);

//   useEffect(() => {
//     if (inputRef.current) {
//       const input = inputRef.current;

//       const handleFocus = () => {
//         // 입력 값의 길이를 가져와서 커서를 이동
//         input.value = "";
//         const length = input.value.length;
//         input.setSelectionRange(length, length);
//       };

//       input.addEventListener("focus", handleFocus);

//       // 컴포넌트가 언마운트될 때 이벤트 리스너 제거
//       return () => {
//         input.removeEventListener("focus", handleFocus);
//       };
//     }
//   }, []);

//   return (
//     <div
//       className={
//         "relative border-2 border-slate-400 flex items-center " + className
//       }
//       style={{ borderRadius: 6 }}
//     >
//       <span
//         className="absolute bg-white leading-none -top-2 sm:-top-[0.5vw] left-1 text-slate-400 text-xs"
//         // style={{ fontSize: 12 }}
//       >
//         {label}
//       </span>
//       <input
//         ref={inputRef}
//         {...props}
//         className="w-full h-full text-left outline-none px-1 py-2"
//         style={{ borderRadius: 6 }}
//       />
//     </div>
//   );
// }

/** 궁합페이지 년월일 입력창 */
export function CustomFormatInputV2(props: InputProps) {
  const { label, className, value, onChange, ...rest } = props;
  const inputRef = useRef<HTMLInputElement>(null);
  const [rawInput, setRawInput] = useState("");
  const [displayValue, setDisplayValue] = useState(() =>
    formatDateInput(String(value), "")
  );

  // Removed the useEffect that reset displayValue from props to prevent flicker

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    const numbersOnly = raw.replace(/\D/g, "").slice(0, 8);
    const formatted = formatDateInput(numbersOnly, raw);
    setRawInput(raw);
    setDisplayValue(formatted);

    if (onChange) {
      const syntheticEvent = {
        ...e,
        target: {
          ...e.target,
          value: numbersOnly,
        },
      } as React.ChangeEvent<HTMLInputElement>;
      onChange(syntheticEvent);
    }
  };

  useEffect(() => {
    if (inputRef.current) {
      const input = inputRef.current;

      const handleFocus = () => {
        // 입력 값의 길이를 가져와서 커서를 이동
        input.value = "";
        const length = input.value.length;
        input.setSelectionRange(length, length);
      };

      input.addEventListener("focus", handleFocus);

      // 컴포넌트가 언마운트될 때 이벤트 리스너 제거
      return () => {
        input.removeEventListener("focus", handleFocus);
      };
    }
  }, []);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const input = inputRef.current;
    if (!input) return;

    const cursorPos = input.selectionStart ?? 0;
    const currentValue = input.value;

    if (
      e.key === "Backspace" &&
      cursorPos > 0 &&
      currentValue[cursorPos - 1] === "/"
    ) {
      e.preventDefault();

      // Remove slash and previous digit
      const newRawInput =
        currentValue.slice(0, cursorPos - 2) + currentValue.slice(cursorPos);
      const numbersOnly = newRawInput.replace(/\D/g, "").slice(0, 8);
      const formatted = formatDateInput(numbersOnly, newRawInput);

      setRawInput(newRawInput);
      setDisplayValue(formatted);

      if (onChange) {
        const syntheticEvent = {
          ...e,
          target: {
            ...input,
            value: numbersOnly,
          },
        } as React.ChangeEvent<HTMLInputElement>;
        onChange(syntheticEvent);
      }

      requestAnimationFrame(() => {
        if (inputRef.current) {
          inputRef.current.setSelectionRange(cursorPos - 2, cursorPos - 2);
        }
      });
    }

    // 부모에서 전달된 onKeyDown도 호출
    if (props.onKeyDown) {
      props.onKeyDown(e);
    }
  };

  return (
    <div
      className={
        "relative rounded-[4px] sm:rounded-md border-[0.1vw] sm:border-[1.8px] border-slate-400 flex items-center " +
        className
      }
    >
      <span className="absolute bg-white leading-none -top-1 sm:-top-[0.5vw] left-1 text-slate-400 text-[7px] sm:text-xs">
        {label}
      </span>
      <input
        ref={inputRef}
        {...rest}
        value={displayValue}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        className="w-full h-full text-xs text-left outline-none sm:text-base py-1 sm:rounded-md rounded-[4px] pl-[3px] sm:pl-[3px]"
      />
    </div>
  );
}

/** 궁합페이지 입력창 */
export function CustomInputV2(props: InputProps) {
  const { label, className } = props;
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (inputRef.current) {
      const input = inputRef.current;

      const handleFocus = () => {
        // 입력 값의 길이를 가져와서 커서를 이동
        input.value = "";
        const length = input.value.length;
        input.setSelectionRange(length, length);
      };

      input.addEventListener("focus", handleFocus);

      // 컴포넌트가 언마운트될 때 이벤트 리스너 제거
      return () => {
        input.removeEventListener("focus", handleFocus);
      };
    }
  }, []);

  return (
    <div
      className={
        "relative rounded-[4px] sm:rounded-md border-[0.1vw] sm:border-[1.8px] border-slate-400 flex items-center " +
        className
      }
    >
      <span className="absolute bg-white leading-none -top-1 sm:-top-[0.5vw] left-1 text-slate-400 text-[7px] sm:text-xs">
        {label}
      </span>
      <input
        ref={inputRef}
        {...props}
        className="w-full h-full text-xs text-left outline-none sm:text-base py-1 sm:rounded-md rounded-[4px] pl-[3px] sm:pl-[3px]"
      />
    </div>
  );
}
/** 마이페이지 입력창 */
export function CustomInputV3(props: InputProps) {
  const { label, className } = props;
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (inputRef.current) {
      const input = inputRef.current;

      const handleFocus = () => {
        // 입력 값의 길이를 가져와서 커서를 이동
        const length = input.value.length;
        input.setSelectionRange(length, length);
      };

      input.addEventListener("focus", handleFocus);

      // 컴포넌트가 언마운트될 때 이벤트 리스너 제거
      return () => {
        input.removeEventListener("focus", handleFocus);
      };
    }
  }, []);

  return (
    <div
      className={
        "relative px-[6px] py-1 border-[1.8px] border-slate-400  flex items-center" +
        className
      }
      style={{ borderRadius: 6 }}
    >
      <span className="absolute bg-white -top-3 left-2 text-slate-400 text-md">
        {label}
      </span>
      <input
        ref={inputRef}
        {...props}
        className="w-full outline-none text-md "
      />
    </div>
  );
}

/** 마이페이지 InfoUpdateDialog 입력창 */
export function CustomInputV4(props: InputProps) {
  const { label, className } = props;
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (inputRef.current) {
      const input = inputRef.current;

      const handleFocus = () => {
        // 입력 값의 길이를 가져와서 커서를 이동
        // 수정 사항:: 입력창 초기화 기능 제거
        // input.value = "";
        const length = input.value.length;
        input.setSelectionRange(length, length);
      };

      input.addEventListener("focus", handleFocus);

      // 컴포넌트가 언마운트될 때 이벤트 리스너 제거
      return () => {
        input.removeEventListener("focus", handleFocus);
      };
    }
  }, []);

  return (
    <div
      className={
        "relative border-2 border-slate-400  flex items-center " + className
      }
      style={{ borderRadius: 6 }}
    >
      <span
        className="absolute bg-white leading-none -top-2 sm:-top-[0.5vw] left-1 text-slate-400 text-xs"
        // style={{ fontSize: 12 }}
      >
        {label}
      </span>
      <input
        ref={inputRef}
        {...props}
        className="w-full text-left outline-none  px-1 py-2"
        style={{ borderRadius: 6 }}
      />
    </div>
  );
}

/** 결제페이지 입력창
 * 휴대폰 번호 입력
 * 01099239397 -> 010-9923-9397
 * 021234567 -> 02-1234-5678
 * 0311234567 -> 031-123-4567
 * 0511234567 -> 051-123-4567
 * 0611234567 -> 061-123-4567
 * 0701234567 -> 070-123-4567
 * 0811234567 -> 081-123-4567
 * 0101234567 -> 010-1234-5678
 * 0111234567 -> 011-1234-5678
 */
function formatPhoneNumberInput(value: string, rawInput: string): string {
  const digits = value.replace(/\D/g, "");
  const len = digits.length;

  if (len < 3) return digits;
  if (len === 3) return `${digits}-`;
  if (len < 7) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  if (len === 7) return `${digits.slice(0, 3)}-${digits.slice(3, 7)}-`;
  if (len < 11)
    return `${digits.slice(0, 3)}-${digits.slice(3, 7)}-${digits.slice(7)}`;
  return `${digits.slice(0, 3)}-${digits.slice(3, 7)}-${digits.slice(7, 11)}`;
}
export function CustomPhoneInput(props: InputProps) {
  const { label, className, value, onChange, ...rest } = props;
  const inputRef = useRef<HTMLInputElement>(null);
  const [rawInput, setRawInput] = useState("");
  const [displayValue, setDisplayValue] = useState(() =>
    formatPhoneNumberInput(String(value ?? ""), "")
  );

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    const numbersOnly = raw.replace(/\D/g, "").slice(0, 11);
    const formatted = formatPhoneNumberInput(numbersOnly, raw);
    setRawInput(raw);
    setDisplayValue(formatted);

    if (onChange) {
      const syntheticEvent = {
        ...e,
        target: {
          ...e.target,
          value: numbersOnly,
        },
      } as React.ChangeEvent<HTMLInputElement>;
      onChange(syntheticEvent);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const input = inputRef.current;
    if (!input) return;

    const cursorPos = input.selectionStart ?? 0;
    const currentValue = input.value;

    if (e.key === "Backspace") {
      // 하이픈 바로 앞에 커서가 있을 때 백스페이스 처리
      if (cursorPos > 0 && currentValue[cursorPos - 1] === "-") {
        e.preventDefault();

        // 하이픈과 그 앞의 숫자를 함께 삭제
        const newValue =
          currentValue.slice(0, cursorPos - 2) + currentValue.slice(cursorPos);
        const numbersOnly = newValue.replace(/\D/g, "").slice(0, 11);
        const formatted = formatPhoneNumberInput(numbersOnly, newValue);

        setRawInput(newValue);
        setDisplayValue(formatted);

        if (onChange) {
          const syntheticEvent = {
            ...e,
            target: {
              ...input,
              value: numbersOnly,
            },
          } as React.ChangeEvent<HTMLInputElement>;
          onChange(syntheticEvent);
        }

        // 커서 위치 조정
        requestAnimationFrame(() => {
          if (inputRef.current) {
            inputRef.current.setSelectionRange(cursorPos - 2, cursorPos - 2);
          }
        });
      }
    }

    // 부모에서 전달된 onKeyDown도 호출
    if (props.onKeyDown) {
      props.onKeyDown(e);
    }
  };

  useEffect(() => {
    setDisplayValue(formatPhoneNumberInput(String(value ?? ""), rawInput));
    // eslint-disable-next-line
  }, [value]);

  return (
    <div className={className}>
      {label && <span className="text-sm text-gray-500 mb-1">{label}</span>}
      <input
        type="tel"
        ref={inputRef}
        {...rest}
        value={displayValue}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        className="w-full h-full text-left outline-none px-1 py-2"
        style={{ borderRadius: 6 }}
      />
    </div>
  );
}
